import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { createChart } from "lightweight-charts";
import ApiClient from "../api/ApiClient";

export const SimulationReportChartView = () => {
  const { id } = useParams();
  const [chartData, setChartData] = useState(null);
  const [currentTickerIndex, setCurrentTickerIndex] = useState(0);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [interval, setInterval] = useState("Day");
  const [candleData, setCandleData] = useState([]);
  const [highlightedNotification, setHighlightedNotification] = useState(null);
  const [highlightedTrade, setHighlightedTrade] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const containerRef = useRef(null);

  // Load simulation report chart data
  useEffect(() => {
    async function loadChartData() {
      try {
        setLoading(true);
        const data = await ApiClient.getSimulationReportChartData(id);
        setChartData(data);

        if (data.TickerData && data.TickerData.length > 0) {
          setSelectedTicker(data.TickerData[0]);
          setCurrentTickerIndex(0);
        }
      } catch (err) {
        console.error("Failed to load chart data:", err);
        setError("Failed to load simulation report data");
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      loadChartData();
    }
  }, [id]);

  // Load candle data when ticker changes
  useEffect(() => {
    async function loadCandleData() {
      if (!selectedTicker) return;

      try {
        setLoading(true);

        // Get date range from trades and notifications
        const allEvents = [
          ...(selectedTicker.Trades || []).map(t => t.Time ? t.Time.toDate() : new Date()),
          ...(selectedTicker.Notifications || []).map(n => n.Time ? n.Time.toDate() : new Date())
        ];

        if (allEvents.length === 0) return;

        const minDate = new Date(Math.min(...allEvents));
        const maxDate = new Date(Math.max(...allEvents));

        // Add some padding (30 days before and after)
        const fromDate = new Date(minDate.getTime() - 30 * 24 * 60 * 60 * 1000);
        const toDate = new Date(maxDate.getTime() + 30 * 24 * 60 * 60 * 1000);

        const response = await ApiClient.searchCandles({
          TickerId: selectedTicker.TickerId,
          Interval: interval,
          TimeFrom: fromDate.toISOString(),
          TimeTo: toDate.toISOString(),
          Limit: 10000
        });

        setCandleData(response.Items || []);
      } catch (err) {
        console.error("Failed to load candle data:", err);
      } finally {
        setLoading(false);
      }
    }

    loadCandleData();
  }, [selectedTicker, interval]);

  // Store click handler ref to avoid multiple subscriptions
  const clickHandlerRef = useRef(null);

  // Function to set up click handler
  const setupClickHandler = useCallback(() => {
    if (!chartRef.current || !selectedTicker) return;

    // Remove previous click handler if exists
    if (clickHandlerRef.current) {
      chartRef.current.unsubscribeClick(clickHandlerRef.current);
    }

    // Create the markers array for reference in click handler
    const markers = [];

    // Add notification markers to reference array
    if (selectedTicker.Notifications) {
      selectedTicker.Notifications.forEach((notification, index) => {
        const timeUnix = notification.Time ? Math.floor(notification.Time.valueOf() / 1000) : 0;
        markers.push({
          time: timeUnix,
          id: `notification_${index}`,
          type: 'notification',
          index: index
        });
      });
    }

    // Add trade markers to reference array
    if (selectedTicker.Trades) {
      selectedTicker.Trades.forEach((trade, index) => {
        const timeUnix = trade.Time ? Math.floor(trade.Time.valueOf() / 1000) : 0;
        markers.push({
          time: timeUnix,
          id: `trade_${index}`,
          type: 'trade',
          index: index
        });
      });
    }

    // Create new click handler
    const clickHandler = (param) => {
      if (param.time) {
        const clickTime = param.time;

        // Find marker by matching time with a larger tolerance for easier clicking
        const marker = markers.find(m => Math.abs(m.time - clickTime) <= 86400); // Allow 1 day tolerance for daily charts
        if (marker) {
          if (marker.type === 'notification') {
            setHighlightedNotification(marker.index);
            setHighlightedTrade(null);
            // Scroll to notification in table
            setTimeout(() => {
              const element = document.getElementById(`notification-${marker.index}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
          } else if (marker.type === 'trade') {
            setHighlightedTrade(marker.index);
            setHighlightedNotification(null);
            // Scroll to trade in table
            setTimeout(() => {
              const element = document.getElementById(`trade-${marker.index}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
          }
        } else {
          // If no exact match, try to find the closest marker within a reasonable range
          let closestMarker = null;
          let closestDistance = Infinity;

          markers.forEach(m => {
            const distance = Math.abs(m.time - clickTime);
            if (distance < closestDistance && distance <= 86400) { // Within 1 day
              closestDistance = distance;
              closestMarker = m;
            }
          });

          if (closestMarker) {
            if (closestMarker.type === 'notification') {
              setHighlightedNotification(closestMarker.index);
              setHighlightedTrade(null);
              setTimeout(() => {
                const element = document.getElementById(`notification-${closestMarker.index}`);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 100);
            } else if (closestMarker.type === 'trade') {
              setHighlightedTrade(closestMarker.index);
              setHighlightedNotification(null);
              setTimeout(() => {
                const element = document.getElementById(`trade-${closestMarker.index}`);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 100);
            }
          } else {
            // Clicked on empty area, clear highlights
            setHighlightedNotification(null);
            setHighlightedTrade(null);
          }
        }
      } else {
        // Clicked on empty area, clear highlights
        setHighlightedNotification(null);
        setHighlightedTrade(null);
      }
    };

    // Subscribe to click events
    chartRef.current.subscribeClick(clickHandler);
    clickHandlerRef.current = clickHandler;
  }, [selectedTicker]);

  // Use useCallback to fix the dependency warning
  const addMarkersToChart = useCallback(() => {
    if (!chartRef.current || !selectedTicker) return;

    const markers = [];

    // Add notification markers
    if (selectedTicker.Notifications) {
      selectedTicker.Notifications.forEach((notification, index) => {
        const timeUnix = notification.Time ? Math.floor(notification.Time.valueOf() / 1000) : 0;
        const isHighlighted = highlightedNotification === index;
        markers.push({
          time: timeUnix,
          position: 'aboveBar',
          color: isHighlighted ? '#FF9800' : '#2196F3', // Orange when highlighted, blue otherwise
          shape: 'circle',
          size: isHighlighted ? 2 : 1 // Larger when highlighted
        });
      });
    }

    // Add trade markers
    if (selectedTicker.Trades) {
      selectedTicker.Trades.forEach((trade, index) => {
        const isBuy = trade.Quantity > 0;
        const timeUnix = trade.Time ? Math.floor(trade.Time.valueOf() / 1000) : 0;
        const isHighlighted = highlightedTrade === index;
        markers.push({
          time: timeUnix,
          position: isBuy ? 'belowBar' : 'aboveBar',
          color: isHighlighted ? '#FF9800' : (isBuy ? '#4CAF50' : '#F44336'), // Orange when highlighted
          shape: isBuy ? 'arrowUp' : 'arrowDown',
          size: isHighlighted ? 2 : 1 // Larger when highlighted
        });
      });
    }

    if (markers.length > 0) {
      // Sort markers by time to ensure ascending order as required by lightweight-charts
      markers.sort((a, b) => a.time - b.time);
      candleSeriesRef.current.setMarkers(markers);
    }
  }, [selectedTicker, highlightedNotification, highlightedTrade]);



  // Initialize chart - only after loading is complete and we have chart data
  useEffect(() => {
    // Don't initialize chart while loading or if no data
    if (loading || !chartData) return;

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const chart = createChart(container, {
      width: container.clientWidth,
      height: 500,
      layout: { background: { color: "#ffffff" }, textColor: "#000" },
      rightPriceScale: { scaleMargins: { top: 0.1, bottom: 0.1 } },
      timeScale: { timeVisible: true, secondsVisible: false },
    });
    chartRef.current = chart;

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#4caf50",
      downColor: "#f44336",
      borderDownColor: "#f44336",
      borderUpColor: "#4caf50",
      wickDownColor: "#f44336",
      wickUpColor: "#4caf50",
    });
    candleSeriesRef.current = candleSeries;

    const handleResize = () => {
      if (!chartRef.current) return;
      chartRef.current.applyOptions({ width: container.clientWidth });
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      // Clean up click handler
      if (chartRef.current && clickHandlerRef.current) {
        chartRef.current.unsubscribeClick(clickHandlerRef.current);
        clickHandlerRef.current = null;
      }
      if (chartRef.current) {
        chartRef.current.remove();
      }
    };
  }, [loading, chartData]);

  // Update chart data when candle data changes
  useEffect(() => {
    if (!candleSeriesRef.current || !candleData.length) return;

    const chartItems = candleData.map(candle => ({
      time: candle.getUnixSeconds(),
      open: Number(candle.Open),
      high: Number(candle.High),
      low: Number(candle.Low),
      close: Number(candle.Close),
    })).sort((a, b) => a.time - b.time);

    try {
      candleSeriesRef.current.setData(chartItems);
      if (chartRef.current) {
        chartRef.current.timeScale().fitContent();
        addMarkersToChart();

        // Set up click handler after chart data is loaded
        setupClickHandler();
      }
    } catch (err) {
      console.error("Failed to update chart:", err);
    }
  }, [candleData, selectedTicker, addMarkersToChart, setupClickHandler]);

  // Update markers when highlights change
  useEffect(() => {
    if (candleSeriesRef.current && selectedTicker) {
      addMarkersToChart();
    }
  }, [highlightedNotification, highlightedTrade, addMarkersToChart, selectedTicker]);

  function cycleTicker(direction) {
    if (!chartData || !chartData.TickerData) return;

    const newIndex = direction === 'next'
      ? (currentTickerIndex + 1) % chartData.TickerData.length
      : (currentTickerIndex - 1 + chartData.TickerData.length) % chartData.TickerData.length;

    setCurrentTickerIndex(newIndex);
    setSelectedTicker(chartData.TickerData[newIndex]);

    // Clear highlights when changing ticker
    setHighlightedNotification(null);
    setHighlightedTrade(null);
  }

  if (loading) {
    return (
      <div className="container mt-4">
        <h2>Simulation Report Chart View</h2>
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <h2>Simulation Report Chart View</h2>
        <div className="alert alert-danger">{error}</div>
        <Link to="/simulation-reports" className="btn btn-secondary">
          Back to Simulation Reports
        </Link>
      </div>
    );
  }

  if (!chartData || !chartData.TickerData || chartData.TickerData.length === 0) {
    return (
      <div className="container mt-4">
        <h2>Simulation Report Chart View</h2>
        <div className="alert alert-info">
          No ticker data available for this simulation report.
        </div>
        <Link to="/simulation-reports" className="btn btn-secondary">
          Back to Simulation Reports
        </Link>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2>Simulation Report #{id} - Chart View</h2>
            <Link to="/simulation-reports" className="btn btn-secondary">
              Back to Reports
            </Link>
          </div>

          {/* Settings Section */}
          {chartData?.Options && (
            <div className="card mb-3">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    Simulation Settings {chartData.DecisionMakerId && `(${chartData.DecisionMakerId})`}
                  </h6>
                  <button
                    className="btn btn-sm btn-link"
                    onClick={() => setShowSettings(!showSettings)}
                  >
                    {showSettings ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              {showSettings && (
                <div className="card-body">
                  <div className="row">
                    {Object.keys(chartData.Options).map((key) => {
                      const value = chartData.Options[key];
                      let displayValue = value;

                      // Format different types of values
                      if (typeof value === 'number') {
                        // Check if it's a percentage-like value (between 0 and 1)
                        if (value > 0 && value < 1) {
                          displayValue = `${(value * 100).toFixed(2)}%`;
                        } else {
                          displayValue = value.toLocaleString();
                        }
                      } else if (typeof value === 'boolean') {
                        displayValue = value ? 'Yes' : 'No';
                      } else if (value === null) {
                        displayValue = 'N/A';
                      } else if (typeof value === 'object') {
                        // Handle objects like TimeSpan
                        displayValue = JSON.stringify(value);
                      }

                      // Convert camelCase/PascalCase to readable format
                      const displayKey = key
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, str => str.toUpperCase())
                        .trim();

                      return (
                        <div key={key} className="col-md-4 col-sm-6 mb-3">
                          <div>
                            <small className="text-muted d-block">{displayKey}</small>
                            <strong>{displayValue}</strong>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Ticker Navigation */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <h5 className="card-title mb-0">
                    {selectedTicker?.TickerCode} - {selectedTicker?.TickerName}
                  </h5>
                  <small className="text-muted">
                    Ticker {currentTickerIndex + 1} of {chartData.TickerData.length}
                  </small>
                </div>
                <div className="col-md-6">
                  <div className="d-flex justify-content-end align-items-center gap-2">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => cycleTicker('prev')}
                      disabled={chartData.TickerData.length <= 1}
                    >
                      ← Previous
                    </button>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => cycleTicker('next')}
                      disabled={chartData.TickerData.length <= 1}
                    >
                      Next →
                    </button>
                    <select
                      className="form-control form-control-sm"
                      style={{ width: "120px" }}
                      value={interval}
                      onChange={(e) => setInterval(e.target.value)}
                    >
                      <option value="Minute">1 min</option>
                      <option value="FiveMinutes">5 min</option>
                      <option value="QuarterHour">15 min</option>
                      <option value="Hour">1 hr</option>
                      <option value="Day">Daily</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="card mb-3">
            <div className="card-body">
              <div style={{ position: "relative", height: 500 }}>
                <div ref={containerRef} style={{ height: 500 }} />
                {loading && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255,255,255,0.8)",
                      zIndex: 1000,
                    }}
                  >
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tooltip for highlighted items */}
          {(highlightedTrade !== null || highlightedNotification !== null) && (
            <div className="alert alert-info mt-3">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>Selected Details:</strong>
                  {highlightedTrade !== null && selectedTicker?.Trades?.[highlightedTrade] && (
                    <div className="mt-2">
                      <h6>Trade #{highlightedTrade + 1}</h6>
                      <p className="mb-1">
                        <strong>Type:</strong> {selectedTicker.Trades[highlightedTrade].getTradeTypeDisplay()}<br/>
                        <strong>Time:</strong> {selectedTicker.Trades[highlightedTrade].getTime()}<br/>
                        <strong>Quantity:</strong> {selectedTicker.Trades[highlightedTrade].getAbsoluteQuantity()}<br/>
                        <strong>Price:</strong> {selectedTicker.Trades[highlightedTrade].getFormattedPrice()}<br/>
                        <strong>Total:</strong> {selectedTicker.Trades[highlightedTrade].getFormattedAmount()}<br/>
                        <strong>Commission:</strong> {selectedTicker.Trades[highlightedTrade].getFormattedCommission()}
                      </p>
                    </div>
                  )}
                  {highlightedNotification !== null && selectedTicker?.Notifications?.[highlightedNotification] && (
                    <div className="mt-2">
                      <h6>Notification #{highlightedNotification + 1}</h6>
                      <p className="mb-1">
                        <strong>Type:</strong> {selectedTicker.Notifications[highlightedNotification].getTypeDisplay()}<br/>
                        <strong>Time:</strong> {selectedTicker.Notifications[highlightedNotification].getTime()}<br/>
                        <strong>Message:</strong> {selectedTicker.Notifications[highlightedNotification].getMessageDisplay()}
                      </p>
                    </div>
                  )}
                </div>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setHighlightedNotification(null);
                    setHighlightedTrade(null);
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Events Summary */}
          <div className="row">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0">
                    Trades ({selectedTicker?.Trades?.length || 0})
                  </h6>
                </div>
                <div className="card-body" style={{ maxHeight: "300px", overflow: "auto" }}>
                  {selectedTicker?.Trades?.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Type</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTicker.Trades.map((trade, idx) => (
                            <tr
                              key={idx}
                              id={`trade-${idx}`}
                              className={highlightedTrade === idx ? 'table-warning' : ''}
                              style={{
                                cursor: 'pointer',
                                transition: 'background-color 0.3s ease'
                              }}
                              onClick={() => {
                                if (highlightedTrade === idx) {
                                  setHighlightedTrade(null);
                                } else {
                                  setHighlightedTrade(idx);
                                  setHighlightedNotification(null);
                                }
                              }}
                            >
                              <td>{trade.getTime()}</td>
                              <td>
                                <span className={`badge ${trade.Quantity > 0 ? 'badge-success' : 'badge-danger'}`}>
                                  {trade.Quantity > 0 ? 'BUY' : 'SELL'}
                                </span>
                              </td>
                              <td>{trade.getAbsoluteQuantity()}</td>
                              <td>{trade.getFormattedPrice()}</td>
                              <td className={trade.TotalAmount >= 0 ? 'text-success' : 'text-danger'}>
                                {trade.getFormattedAmount()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted">No trades for this ticker</p>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0">
                    Notifications ({selectedTicker?.Notifications?.length || 0})
                  </h6>
                </div>
                <div className="card-body" style={{ maxHeight: "300px", overflow: "auto" }}>
                  {selectedTicker?.Notifications?.length > 0 ? (
                    <div className="list-group list-group-flush">
                      {selectedTicker.Notifications.map((notification, idx) => (
                        <div
                          key={idx}
                          id={`notification-${idx}`}
                          className={`list-group-item px-0 ${highlightedNotification === idx ? 'bg-warning' : ''}`}
                          style={{
                            cursor: 'pointer',
                            transition: 'background-color 0.3s ease'
                          }}
                          onClick={() => {
                            if (highlightedNotification === idx) {
                              setHighlightedNotification(null);
                            } else {
                              setHighlightedNotification(idx);
                              setHighlightedTrade(null);
                            }
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <span className={"badge text-muted mr-2"}>
                                {notification.getTypeDisplay()}
                              </span>
                              <small className="text-muted">
                                {notification.getTime()}
                              </small>
                            </div>
                          </div>
                          <p className="mb-1 mt-2">{notification.getMessageDisplay()}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No notifications for this ticker</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulationReportChartView;